import { datadogLogs } from '@datadog/browser-logs';
import { useCallback, useRef, useState } from 'react';
import { useModelChangeProposal } from './useModelChangeProposal';

export type LeavingWithProposal = {
  /**
   * Call in place of leaving. Asks first when the advisor is walking away from
   * a proposal, and leaves straight away when there is none to answer for.
   */
  leave: (exit: () => void) => void;
  /** Whether the advisor is being asked what to do with the proposal. */
  asking: boolean;
  /** Whether the proposal is being called back. */
  discarding: boolean;
  /** Leave, and let the proposal stand. */
  keep: () => void;
  /** Call the proposal back, then leave. */
  discard: () => Promise<void>;
};

/**
 * What becomes of a proposal the advisor walks away from.
 *
 * A household can only have one proposal open at a time, so one left behind by
 * an abandoned workflow is not merely untidy: the next attempt to change that
 * household is refused until someone signs or cancels it. The advisor is asked
 * rather than told, because a proposal already out for signature may be one the
 * client is part way through, and only the advisor knows whether this is a
 * change of mind or a step away from a change still meant.
 */
export const useLeavingWithProposal = ({
  proposalLeftBehind,
  onDiscarded,
}: {
  /**
   * Asked at the moment of leaving rather than read from the last render: a
   * workflow finishes and closes itself in a single breath, and what it leaves
   * behind on the way out is not abandoned at all.
   */
  proposalLeftBehind: () => string | undefined;
  /** Run after the proposal is called back, before leaving. */
  onDiscarded?: (modelChangeProposalId: string) => Promise<void> | void;
}): LeavingWithProposal => {
  const { cancelProposal } = useModelChangeProposal();
  const [asking, setAsking] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const exitRef = useRef<(() => void) | undefined>(undefined);
  const askingForRef = useRef<string | undefined>(undefined);

  const latest = useRef({ proposalLeftBehind, onDiscarded });
  latest.current = { proposalLeftBehind, onDiscarded };

  const leaveNow = useCallback(() => {
    setAsking(false);
    askingForRef.current = undefined;
    const exit = exitRef.current;
    exitRef.current = undefined;
    exit?.();
  }, []);

  const leave = useCallback((exit: () => void) => {
    const modelChangeProposalId = latest.current.proposalLeftBehind();
    if (!modelChangeProposalId) {
      exit();
      return;
    }
    exitRef.current = exit;
    askingForRef.current = modelChangeProposalId;
    setAsking(true);
  }, []);

  const discard = useCallback(async () => {
    const modelChangeProposalId = askingForRef.current;
    if (!modelChangeProposalId) {
      leaveNow();
      return;
    }

    setDiscarding(true);
    try {
      await cancelProposal(modelChangeProposalId);
      await latest.current.onDiscarded?.(modelChangeProposalId);
    } catch (caught) {
      // The drawer is on its way out and the advisor has nothing left to press,
      // so a proposal that would not be called back is left standing — which is
      // the state they were leaving it in a moment ago either way.
      datadogLogs.logger.error(
        'Discarding household model change proposal failed',
        { modelChangeProposalId },
        caught instanceof Error ? caught : undefined,
      );
    } finally {
      setDiscarding(false);
      leaveNow();
    }
  }, [cancelProposal, leaveNow]);

  return { leave, asking, discarding, keep: leaveNow, discard };
};
